import { useState, useEffect } from 'react'
import { Copy, Check, Database, Table, Play, ChevronLeft, ChevronRight } from 'lucide-react'
import { synapseAPI } from '../lib/synapse-api'

const LOG_SERVER = 'http://localhost:3101'

interface ClaudeConfig {
  personality: string
  snippets: string
  memory: string
}

const SCHEMA_SQL = `-- ============================================================================
-- SOLARIS EDUCATION PLATFORM SCHEMA
-- ============================================================================

-- Courses
CREATE TABLE IF NOT EXISTS courses (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE,
  title TEXT NOT NULL,
  description TEXT,
  thumbnail_url TEXT,
  difficulty TEXT CHECK (difficulty IN ('beginner', 'intermediate', 'advanced')) DEFAULT 'beginner',
  category TEXT,
  estimated_duration_minutes INTEGER DEFAULT 30,
  is_published BOOLEAN DEFAULT false,
  order_index INTEGER DEFAULT 0,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- Lessons within courses
CREATE TABLE IF NOT EXISTS lessons (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  course_id UUID REFERENCES courses(id) ON DELETE CASCADE NOT NULL,
  title TEXT NOT NULL,
  content TEXT,
  video_url TEXT,
  order_index INTEGER DEFAULT 0,
  estimated_duration_minutes INTEGER DEFAULT 10,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- User progress on courses/lessons
CREATE TABLE IF NOT EXISTS user_course_progress (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  course_id UUID REFERENCES courses(id) ON DELETE CASCADE NOT NULL,
  current_lesson_id UUID REFERENCES lessons(id) ON DELETE SET NULL,
  completed_lessons UUID[] DEFAULT '{}',
  progress_percent INTEGER DEFAULT 0,
  started_at TIMESTAMPTZ DEFAULT NOW(),
  completed_at TIMESTAMPTZ,
  last_accessed_at TIMESTAMPTZ DEFAULT NOW(),
  UNIQUE(user_id, course_id)
);

-- Quizzes
CREATE TABLE IF NOT EXISTS quizzes (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  course_id UUID REFERENCES courses(id) ON DELETE CASCADE,
  lesson_id UUID REFERENCES lessons(id) ON DELETE CASCADE,
  title TEXT NOT NULL,
  description TEXT,
  category TEXT,
  difficulty TEXT CHECK (difficulty IN ('easy', 'medium', 'hard')) DEFAULT 'medium',
  time_limit_seconds INTEGER,
  passing_score INTEGER DEFAULT 70,
  is_published BOOLEAN DEFAULT false,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- Quiz questions
CREATE TABLE IF NOT EXISTS quiz_questions (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  quiz_id UUID REFERENCES quizzes(id) ON DELETE CASCADE NOT NULL,
  question_text TEXT NOT NULL,
  question_type TEXT CHECK (question_type IN ('multiple_choice', 'true_false', 'fill_blank')) DEFAULT 'multiple_choice',
  options JSONB DEFAULT '[]',
  correct_answer TEXT,
  explanation TEXT,
  points INTEGER DEFAULT 10,
  order_index INTEGER DEFAULT 0,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- User quiz attempts
CREATE TABLE IF NOT EXISTS quiz_attempts (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  quiz_id UUID REFERENCES quizzes(id) ON DELETE CASCADE NOT NULL,
  score INTEGER DEFAULT 0,
  max_score INTEGER DEFAULT 0,
  percentage INTEGER DEFAULT 0,
  passed BOOLEAN DEFAULT false,
  answers JSONB DEFAULT '{}',
  time_taken_seconds INTEGER,
  started_at TIMESTAMPTZ DEFAULT NOW(),
  completed_at TIMESTAMPTZ
);

-- Achievements / Badges
CREATE TABLE IF NOT EXISTS achievements (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  name TEXT NOT NULL UNIQUE,
  description TEXT,
  icon TEXT,
  category TEXT,
  requirement_type TEXT,
  requirement_value INTEGER DEFAULT 1,
  xp_reward INTEGER DEFAULT 100,
  is_hidden BOOLEAN DEFAULT false,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- User achievements
CREATE TABLE IF NOT EXISTS user_achievements (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  achievement_id UUID REFERENCES achievements(id) ON DELETE CASCADE NOT NULL,
  earned_at TIMESTAMPTZ DEFAULT NOW(),
  UNIQUE(user_id, achievement_id)
);

-- User streaks and XP
CREATE TABLE IF NOT EXISTS user_stats (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL UNIQUE,
  total_xp INTEGER DEFAULT 0,
  current_streak INTEGER DEFAULT 0,
  longest_streak INTEGER DEFAULT 0,
  last_activity_date DATE,
  courses_completed INTEGER DEFAULT 0,
  lessons_completed INTEGER DEFAULT 0,
  quizzes_passed INTEGER DEFAULT 0,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- ============================================================================
-- LIGHTSITE PAGE BUILDER SCHEMA
-- ============================================================================

CREATE TABLE IF NOT EXISTS lightsite_projects (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  name TEXT NOT NULL,
  description TEXT,
  template_id TEXT,
  config JSONB DEFAULT '{}',
  components JSONB DEFAULT '[]',
  theme JSONB DEFAULT '{}',
  status TEXT CHECK (status IN ('draft', 'building', 'ready', 'deployed')) DEFAULT 'draft',
  preview_url TEXT,
  deploy_url TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS lightsite_builds (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  project_id UUID REFERENCES lightsite_projects(id) ON DELETE CASCADE NOT NULL,
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  synapse_task_id TEXT,
  status TEXT CHECK (status IN ('queued', 'building', 'success', 'failed')) DEFAULT 'queued',
  config_snapshot JSONB DEFAULT '{}',
  output_files JSONB DEFAULT '[]',
  build_log TEXT,
  tokens_used INTEGER DEFAULT 0,
  cost_usd NUMERIC(10,4) DEFAULT 0,
  duration_ms INTEGER,
  started_at TIMESTAMPTZ,
  completed_at TIMESTAMPTZ,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS lightsite_templates (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE SET NULL,
  name TEXT NOT NULL,
  description TEXT,
  category TEXT,
  thumbnail_url TEXT,
  config JSONB DEFAULT '{}',
  components JSONB DEFAULT '[]',
  theme JSONB DEFAULT '{}',
  is_public BOOLEAN DEFAULT false,
  use_count INTEGER DEFAULT 0,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- ============================================================================
-- LIGHTGRID COMPONENT BUILDER SCHEMA
-- ============================================================================

CREATE TABLE IF NOT EXISTS lightgrid_layouts (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  name TEXT NOT NULL,
  description TEXT,
  grid_config JSONB DEFAULT '{}',
  cells JSONB DEFAULT '[]',
  is_template BOOLEAN DEFAULT false,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS lightgrid_components (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  name TEXT NOT NULL,
  description TEXT,
  category TEXT,
  code TEXT NOT NULL,
  props_schema JSONB DEFAULT '{}',
  preview_html TEXT,
  is_public BOOLEAN DEFAULT false,
  use_count INTEGER DEFAULT 0,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS lightgrid_exports (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES auth.users(id) ON DELETE CASCADE NOT NULL,
  layout_id UUID REFERENCES lightgrid_layouts(id) ON DELETE SET NULL,
  export_type TEXT CHECK (export_type IN ('react', 'html', 'json')) DEFAULT 'react',
  code TEXT,
  synapse_task_id TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- INDEXES
CREATE INDEX IF NOT EXISTS idx_courses_user ON courses(user_id);
CREATE INDEX IF NOT EXISTS idx_courses_category ON courses(category);
CREATE INDEX IF NOT EXISTS idx_lessons_course ON lessons(course_id);
CREATE INDEX IF NOT EXISTS idx_user_course_progress_user ON user_course_progress(user_id);
CREATE INDEX IF NOT EXISTS idx_quizzes_course ON quizzes(course_id);
CREATE INDEX IF NOT EXISTS idx_quiz_questions_quiz ON quiz_questions(quiz_id);
CREATE INDEX IF NOT EXISTS idx_quiz_attempts_user ON quiz_attempts(user_id);
CREATE INDEX IF NOT EXISTS idx_user_achievements_user ON user_achievements(user_id);
CREATE INDEX IF NOT EXISTS idx_lightsite_projects_user ON lightsite_projects(user_id);
CREATE INDEX IF NOT EXISTS idx_lightsite_builds_project ON lightsite_builds(project_id);
CREATE INDEX IF NOT EXISTS idx_lightgrid_layouts_user ON lightgrid_layouts(user_id);

-- RLS (enable on all tables)
ALTER TABLE courses ENABLE ROW LEVEL SECURITY;
ALTER TABLE lessons ENABLE ROW LEVEL SECURITY;
ALTER TABLE user_course_progress ENABLE ROW LEVEL SECURITY;
ALTER TABLE quizzes ENABLE ROW LEVEL SECURITY;
ALTER TABLE quiz_questions ENABLE ROW LEVEL SECURITY;
ALTER TABLE quiz_attempts ENABLE ROW LEVEL SECURITY;
ALTER TABLE achievements ENABLE ROW LEVEL SECURITY;
ALTER TABLE user_achievements ENABLE ROW LEVEL SECURITY;
ALTER TABLE user_stats ENABLE ROW LEVEL SECURITY;
ALTER TABLE lightsite_projects ENABLE ROW LEVEL SECURITY;
ALTER TABLE lightsite_builds ENABLE ROW LEVEL SECURITY;
ALTER TABLE lightsite_templates ENABLE ROW LEVEL SECURITY;
ALTER TABLE lightgrid_layouts ENABLE ROW LEVEL SECURITY;
ALTER TABLE lightgrid_components ENABLE ROW LEVEL SECURITY;
ALTER TABLE lightgrid_exports ENABLE ROW LEVEL SECURITY;

-- RLS POLICIES (idempotent - drop before create)
DROP POLICY IF EXISTS "Users can view published courses" ON courses;
DROP POLICY IF EXISTS "Users can manage own courses" ON courses;
DROP POLICY IF EXISTS "Users can view lessons of published courses" ON lessons;
DROP POLICY IF EXISTS "Users can manage own progress" ON user_course_progress;
DROP POLICY IF EXISTS "Users can view published quizzes" ON quizzes;
DROP POLICY IF EXISTS "Users can manage own quiz attempts" ON quiz_attempts;
DROP POLICY IF EXISTS "Anyone can view achievements" ON achievements;
DROP POLICY IF EXISTS "Users can view own achievements" ON user_achievements;
DROP POLICY IF EXISTS "Users can manage own stats" ON user_stats;
DROP POLICY IF EXISTS "Users can manage own projects" ON lightsite_projects;
DROP POLICY IF EXISTS "Users can manage own builds" ON lightsite_builds;
DROP POLICY IF EXISTS "Users can view public templates" ON lightsite_templates;
DROP POLICY IF EXISTS "Users can manage own templates" ON lightsite_templates;
DROP POLICY IF EXISTS "Users can manage own layouts" ON lightgrid_layouts;
DROP POLICY IF EXISTS "Users can view public components" ON lightgrid_components;
DROP POLICY IF EXISTS "Users can manage own components" ON lightgrid_components;
DROP POLICY IF EXISTS "Users can manage own exports" ON lightgrid_exports;

CREATE POLICY "Users can view published courses" ON courses FOR SELECT USING (is_published = true OR user_id = auth.uid());
CREATE POLICY "Users can manage own courses" ON courses FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can view lessons of published courses" ON lessons FOR SELECT USING (EXISTS (SELECT 1 FROM courses WHERE courses.id = lessons.course_id AND (courses.is_published = true OR courses.user_id = auth.uid())));
CREATE POLICY "Users can manage own progress" ON user_course_progress FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can view published quizzes" ON quizzes FOR SELECT USING (is_published = true);
CREATE POLICY "Users can manage own quiz attempts" ON quiz_attempts FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Anyone can view achievements" ON achievements FOR SELECT USING (true);
CREATE POLICY "Users can view own achievements" ON user_achievements FOR SELECT USING (user_id = auth.uid());
CREATE POLICY "Users can manage own stats" ON user_stats FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can manage own projects" ON lightsite_projects FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can manage own builds" ON lightsite_builds FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can view public templates" ON lightsite_templates FOR SELECT USING (is_public = true OR user_id = auth.uid());
CREATE POLICY "Users can manage own templates" ON lightsite_templates FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can manage own layouts" ON lightgrid_layouts FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can view public components" ON lightgrid_components FOR SELECT USING (is_public = true OR user_id = auth.uid());
CREATE POLICY "Users can manage own components" ON lightgrid_components FOR ALL USING (user_id = auth.uid());
CREATE POLICY "Users can manage own exports" ON lightgrid_exports FOR ALL USING (user_id = auth.uid());

-- ============================================================================
-- PIPELINE & METRICS SCHEMA
-- ============================================================================

CREATE TABLE IF NOT EXISTS pipeline_tasks (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  task_id TEXT UNIQUE NOT NULL,
  user_id UUID REFERENCES profiles(id),
  description TEXT NOT NULL,
  status TEXT NOT NULL DEFAULT 'queued',
  priority TEXT DEFAULT 'normal',
  phase TEXT DEFAULT 'intake',
  progress INT DEFAULT 0,
  spec JSONB DEFAULT '{}',
  assigned_worker TEXT,
  worker_model TEXT,
  tokens_in INT DEFAULT 0,
  tokens_out INT DEFAULT 0,
  cost_usd DECIMAL(10,6) DEFAULT 0,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  started_at TIMESTAMPTZ,
  updated_at TIMESTAMPTZ DEFAULT NOW(),
  error_message TEXT,
  retry_count INT DEFAULT 0
);

CREATE TABLE IF NOT EXISTS task_history (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  task_id TEXT UNIQUE NOT NULL,
  user_id UUID REFERENCES profiles(id),
  description TEXT,
  status TEXT NOT NULL,
  result JSONB,
  files_generated JSONB DEFAULT '[]',
  tokens_used INT DEFAULT 0,
  cost_usd DECIMAL(10,6) DEFAULT 0,
  qa_score INT,
  qa_passed BOOLEAN,
  qa_issues JSONB DEFAULT '[]',
  created_at TIMESTAMPTZ,
  completed_at TIMESTAMPTZ,
  deployed_at TIMESTAMPTZ,
  duration_ms INT,
  source TEXT DEFAULT 'pipeline',
  source_file TEXT
);

CREATE TABLE IF NOT EXISTS llm_calls (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  task_id TEXT,
  user_id UUID REFERENCES profiles(id),
  model TEXT NOT NULL,
  provider TEXT DEFAULT 'anthropic',
  tokens_in INT NOT NULL DEFAULT 0,
  tokens_out INT NOT NULL DEFAULT 0,
  cost_usd DECIMAL(10,6) DEFAULT 0,
  latency_ms INT,
  endpoint TEXT,
  request_type TEXT,
  success BOOLEAN DEFAULT true,
  error_message TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS custom_agents (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_id UUID REFERENCES profiles(id),
  name TEXT NOT NULL,
  icon TEXT,
  color TEXT,
  model TEXT NOT NULL,
  system_prompt TEXT,
  temperature FLOAT DEFAULT 0.7,
  max_tokens INT DEFAULT 4096,
  skills TEXT[] DEFAULT '{}',
  tools TEXT[] DEFAULT '{}',
  is_active BOOLEAN DEFAULT true,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS system_metrics (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  cpu_percent FLOAT,
  memory_mb FLOAT,
  active_tasks INT,
  queue_depth INT,
  workers_active INT,
  workers_total INT,
  daily_spend DECIMAL(10,6),
  monthly_spend DECIMAL(10,6),
  tasks_completed_hour INT,
  api_calls_minute INT,
  error_count INT,
  error_rate FLOAT,
  recorded_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE INDEX IF NOT EXISTS idx_pipeline_tasks_status ON pipeline_tasks(status);
CREATE INDEX IF NOT EXISTS idx_task_history_status ON task_history(status);
CREATE INDEX IF NOT EXISTS idx_llm_calls_task ON llm_calls(task_id);
CREATE INDEX IF NOT EXISTS idx_system_metrics_time ON system_metrics(recorded_at DESC);

ALTER TABLE pipeline_tasks ENABLE ROW LEVEL SECURITY;
ALTER TABLE task_history ENABLE ROW LEVEL SECURITY;
ALTER TABLE llm_calls ENABLE ROW LEVEL SECURITY;
ALTER TABLE custom_agents ENABLE ROW LEVEL SECURITY;
ALTER TABLE system_metrics ENABLE ROW LEVEL SECURITY;

DROP POLICY IF EXISTS "Users own pipeline_tasks" ON pipeline_tasks;
DROP POLICY IF EXISTS "Users own task_history" ON task_history;
DROP POLICY IF EXISTS "Users own llm_calls" ON llm_calls;
DROP POLICY IF EXISTS "Users own custom_agents" ON custom_agents;
DROP POLICY IF EXISTS "Read metrics" ON system_metrics;
DROP POLICY IF EXISTS "Insert metrics" ON system_metrics;

CREATE POLICY "Users own pipeline_tasks" ON pipeline_tasks FOR ALL USING (user_id = auth.uid() OR user_id IS NULL);
CREATE POLICY "Users own task_history" ON task_history FOR ALL USING (user_id = auth.uid() OR user_id IS NULL);
CREATE POLICY "Users own llm_calls" ON llm_calls FOR ALL USING (user_id = auth.uid() OR user_id IS NULL);
CREATE POLICY "Users own custom_agents" ON custom_agents FOR ALL USING (user_id = auth.uid() OR user_id IS NULL);
CREATE POLICY "Read metrics" ON system_metrics FOR SELECT USING (auth.role() = 'authenticated');
CREATE POLICY "Insert metrics" ON system_metrics FOR INSERT WITH CHECK (true);
`;

interface TableSchema {
  table: string
  columns: { name: string; type: string; pk: boolean }[]
  rowCount: number
}

interface DbRow {
  [key: string]: unknown
}

export default function ClaudePage() {
  const [config, setConfig] = useState<ClaudeConfig | null>(null)
  const [activeTab, setActiveTab] = useState<'personality' | 'snippets' | 'memory' | 'submit' | 'schema' | 'database'>('personality')
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [taskDescription, setTaskDescription] = useState('')
  const [taskStatus, setTaskStatus] = useState('')

  // Database viewer state
  const [tables, setTables] = useState<string[]>([])
  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [tableSchema, setTableSchema] = useState<TableSchema | null>(null)
  const [tableData, setTableData] = useState<DbRow[]>([])
  const [dbOffset, setDbOffset] = useState(0)
  const [dbLimit] = useState(50)
  const [customQuery, setCustomQuery] = useState('')
  const [queryResult, setQueryResult] = useState<DbRow[] | null>(null)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [dbLoading, setDbLoading] = useState(false)

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    setLoading(true)
    try {
      // Load CLAUDE.md, SNIPPETS.md, memory.md via API
      const [personality, snippets, memory] = await Promise.all([
        synapseAPI.getClaudeConfig('personality'),
        synapseAPI.getClaudeConfig('snippets'),
        synapseAPI.getClaudeConfig('memory'),
      ])

      setConfig({ personality, snippets, memory })
    } catch (error) {
      console.error('Failed to load config:', error)
      setConfig({
        personality: '# Error loading config',
        snippets: '# Error loading snippets',
        memory: '# Error loading memory',
      })
    }
    setLoading(false)
  }

  const saveConfig = async (file: string, content: string) => {
    setSaving(true)
    try {
      await synapseAPI.saveClaudeConfig(file as 'personality' | 'snippets' | 'memory', content)
    } catch (error) {
      console.error('Failed to save:', error)
    }
    setSaving(false)
  }

  const submitTask = async () => {
    if (!taskDescription.trim()) return
    setTaskStatus('Submitting...')
    try {
      const result = await synapseAPI.submitTask(taskDescription, 'NORMAL')
      setTaskStatus(`Submitted: ${result.task_id}`)
      setTaskDescription('')
    } catch {
      setTaskStatus('Error submitting task')
    }
  }

  const downloadBackup = () => {
    window.open('/mothership-backup.zip', '_blank')
  }

  // Database viewer functions
  const loadTables = async () => {
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/tables`)
      const data = await res.json()
      setTables(data.tables || [])
    } catch (e) {
      console.error('Failed to load tables:', e)
    }
  }

  const loadTableSchema = async (table: string) => {
    setDbLoading(true)
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/schema/${table}`)
      const data = await res.json()
      setTableSchema(data)
    } catch (e) {
      console.error('Failed to load schema:', e)
    }
    setDbLoading(false)
  }

  const loadTableData = async (table: string, offset = 0) => {
    setDbLoading(true)
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/data/${table}?limit=${dbLimit}&offset=${offset}`)
      const data = await res.json()
      setTableData(data.rows || [])
      setDbOffset(offset)
    } catch (e) {
      console.error('Failed to load data:', e)
    }
    setDbLoading(false)
  }

  const selectTable = (table: string) => {
    setSelectedTable(table)
    setQueryResult(null)
    setQueryError(null)
    loadTableSchema(table)
    loadTableData(table, 0)
  }

  const runCustomQuery = async () => {
    if (!customQuery.trim()) return
    setDbLoading(true)
    setQueryError(null)
    try {
      const res = await fetch(`${LOG_SERVER}/api/db/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: customQuery })
      })
      const data = await res.json()
      if (data.error) {
        setQueryError(data.error)
        setQueryResult(null)
      } else {
        setQueryResult(data.rows)
        setQueryError(null)
      }
    } catch (e) {
      setQueryError(String(e))
    }
    setDbLoading(false)
  }

  // Load tables when database tab is selected
  useEffect(() => {
    if (activeTab === 'database' && tables.length === 0) {
      loadTables()
    }
  }, [activeTab])

  if (loading) {
    return (
      <main className="flex-1 p-6 flex items-center justify-center">
        <div className="text-gray-400">Loading Claude config...</div>
      </main>
    )
  }

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Claude Settings</h1>
          <button
            onClick={downloadBackup}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium flex items-center gap-2"
          >
            <span>Download Backup</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {(['personality', 'snippets', 'memory', 'submit', 'schema', 'database'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize flex items-center gap-2 ${
                activeTab === tab
                  ? 'bg-blue-600 text-white'
                  : 'bg-[#21262d] text-gray-400 hover:bg-[#2d333b]'
              }`}
            >
              {tab === 'database' && <Database size={16} />}
              {tab === 'submit' ? 'Submit Task' : tab}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'submit' ? (
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-6 space-y-4">
            <h2 className="text-lg font-semibold">Submit Task to Pipeline</h2>
            <textarea
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Enter task description..."
              className="w-full h-48 bg-[#0d1117] border border-[#30363d] rounded-lg p-4 text-white font-mono resize-none focus:outline-none focus:border-blue-500"
            />
            <div className="flex items-center gap-4">
              <button
                onClick={submitTask}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium"
              >
                Submit
              </button>
              {taskStatus && <span className="text-gray-400">{taskStatus}</span>}
            </div>
          </div>
        ) : activeTab === 'schema' ? (
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-[#30363d] flex items-center justify-between">
              <span className="font-medium">Solaris + LightSite + LightGrid Schema</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(SCHEMA_SQL)
                  setCopied(true)
                  setTimeout(() => setCopied(false), 2000)
                }}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm flex items-center gap-2"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied!' : 'Copy SQL'}
              </button>
            </div>
            <pre className="w-full h-[60vh] bg-[#0d1117] p-4 text-white font-mono text-xs overflow-auto">
              {SCHEMA_SQL}
            </pre>
          </div>
        ) : activeTab === 'database' ? (
          <div className="flex gap-4 h-[70vh]">
            {/* Table List Sidebar */}
            <div className="w-48 bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden flex flex-col">
              <div className="px-3 py-2 border-b border-[#30363d] font-medium text-sm flex items-center gap-2">
                <Database size={14} /> Tables
              </div>
              <div className="flex-1 overflow-y-auto">
                {tables.map((table) => (
                  <button
                    key={table}
                    onClick={() => selectTable(table)}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#21262d] ${
                      selectedTable === table ? 'bg-blue-600/20 text-blue-400' : 'text-gray-300'
                    }`}
                  >
                    <Table size={12} /> {table}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col gap-4">
              {/* Custom Query */}
              <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customQuery}
                    onChange={(e) => setCustomQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && runCustomQuery()}
                    placeholder="SELECT * FROM table_name LIMIT 10"
                    className="flex-1 bg-[#0d1117] border border-[#30363d] rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500"
                  />
                  <button
                    onClick={runCustomQuery}
                    disabled={dbLoading}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded flex items-center gap-2 text-sm"
                  >
                    <Play size={14} /> Run
                  </button>
                </div>
                {queryError && (
                  <div className="mt-2 text-red-400 text-sm font-mono">{queryError}</div>
                )}
              </div>

              {/* Schema Info */}
              {tableSchema && !queryResult && (
                <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{tableSchema.table}</span>
                    <span className="text-sm text-gray-400">{tableSchema.rowCount.toLocaleString()} rows</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tableSchema.columns.map((col) => (
                      <span
                        key={col.name}
                        className={`px-2 py-1 rounded text-xs font-mono ${
                          col.pk ? 'bg-yellow-600/20 text-yellow-400' : 'bg-[#21262d] text-gray-300'
                        }`}
                      >
                        {col.name} <span className="text-gray-500">{col.type}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Data Table */}
              <div className="flex-1 bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden flex flex-col">
                <div className="flex-1 overflow-auto">
                  {dbLoading ? (
                    <div className="flex items-center justify-center h-full text-gray-400">Loading...</div>
                  ) : (queryResult || tableData).length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-[#21262d] sticky top-0">
                        <tr>
                          {Object.keys((queryResult || tableData)[0]).map((col) => (
                            <th key={col} className="px-3 py-2 text-left font-medium text-gray-300 border-b border-[#30363d]">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(queryResult || tableData).map((row, i) => (
                          <tr key={i} className="hover:bg-[#21262d]/50">
                            {Object.values(row).map((val, j) => (
                              <td key={j} className="px-3 py-2 border-b border-[#30363d]/50 font-mono text-xs max-w-xs truncate">
                                {val === null ? <span className="text-gray-500">NULL</span> : String(val)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      {selectedTable ? 'No data' : 'Select a table'}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {!queryResult && selectedTable && tableSchema && (
                  <div className="px-3 py-2 border-t border-[#30363d] flex items-center justify-between text-sm">
                    <span className="text-gray-400">
                      Showing {dbOffset + 1}-{Math.min(dbOffset + dbLimit, tableSchema.rowCount)} of {tableSchema.rowCount.toLocaleString()}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => loadTableData(selectedTable, Math.max(0, dbOffset - dbLimit))}
                        disabled={dbOffset === 0 || dbLoading}
                        className="px-3 py-1 bg-[#21262d] hover:bg-[#2d333b] disabled:opacity-50 rounded flex items-center gap-1"
                      >
                        <ChevronLeft size={14} /> Prev
                      </button>
                      <button
                        onClick={() => loadTableData(selectedTable, dbOffset + dbLimit)}
                        disabled={dbOffset + dbLimit >= tableSchema.rowCount || dbLoading}
                        className="px-3 py-1 bg-[#21262d] hover:bg-[#2d333b] disabled:opacity-50 rounded flex items-center gap-1"
                      >
                        Next <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-[#30363d] flex items-center justify-between">
              <span className="font-medium capitalize">{activeTab}.md</span>
              <button
                onClick={() => config && saveConfig(activeTab, config[activeTab as keyof ClaudeConfig])}
                disabled={saving}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded text-sm"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
            <textarea
              value={config?.[activeTab as keyof ClaudeConfig] || ''}
              onChange={(e) => setConfig(prev => prev ? { ...prev, [activeTab]: e.target.value } : null)}
              className="w-full h-[60vh] bg-[#0d1117] p-4 text-white font-mono text-sm resize-none focus:outline-none"
            />
          </div>
        )}
      </div>
    </main>
  )
}
